<template>
  <div class="ticket-create-new-ticket__container">
    <organism-ticket-create-new-ticket
      v-if="!isLoading"
      :customerIdcIdList="customerIdcIdList"
      :departmentList="departmentList"
      :priorityList="priorityList"
      :sourceList="sourceList"
      :topicList="topicList"
      :customerData="customerData"
      :ticketData="ticketData"
      :escalation="escalationSelected"
      :escalationDetail="escalationDetail"
      :escalationTemplateList="escalationTemplateList"
      @changeEscalationTemplate="changeEscalationTemplate"
      @selectedIdcId="getCustomerData"
      @selectedTopicId="getTicketData"
      @createTicket="createTicketToApi"
    ></organism-ticket-create-new-ticket>
  </div>
</template>

<script>
import {
  getContractByIdcId,
  getTopicList,
  getTopicById,
  getContractNameList,
  createNewTicket,
  uploadTicketAttachments,
} from "@/api/api-module1"
import mixins from "@/mixins"
import OrganismTicketCreateNewTicket from "@/components/organisms/portal/internal/ticket/OrganismTicketCreateNewTicket.vue"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import { getDepartmentListBySiteId } from "@/api/api-module-data"
import {
  getTicketEscalationActiveList,
  getTicketEscalationById,
} from "@/api/module-1/escalation"
import { mapGetters } from "vuex"

export default {
  name: "TicketCreateNewTicket",
  components: { OrganismTicketCreateNewTicket },
  mixins: [mixins, mixinsSiteHelper],
  data() {
    return {
      // customer
      customerIdcIdList: null,
      customerData: null,

      // ticket detail
      departmentList: [],
      priorityList: [
        { id: "CRITICAL", name: "Critical" },
        { id: "HIGH", name: "High" },
        { id: "MEDIUM", name: "Medium" },
        { id: "LOW", name: "Low" },
      ],
      sourceList: [
        { id: "TICKET", name: "Ticket" },
        { id: "PHONE", name: "Phone" },
        { id: "EMAIL", name: "Email" },
        { id: "OTHER", name: "Other" },
      ],

      // ticket
      topicList: null,
      ticketData: null,
      // escalation
      escalationTemplateList: null,
      escalationSelected: null,
      escalationDetail: {
        name: "",
        description: "",
        levels: [],
      },
    }
  },
  computed: {
    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),

    isLoading() {
      return !this.customerIdcIdList || !this.departmentList || !this.topicList
    },
  },
  watch: {
    async siteSetting() {
      await this.getDataFromApi()
    },
  },
  async created() {
    await this.getTicketEscalationActiveList()
    await this.getDataFromApi()
  },
  methods: {
    // get data from api
    async getDataFromApi() {
      await Promise.all([
        this.getTopicListFromApi(),
        this.getIdcIdListFromApi(),
      ])
    },

    // get idc id list
    async getIdcIdListFromApi() {
      this.showLoading(true)
      this.customerIdcIdList = []
      const filter = {
        siteIds: this.siteOptions.map((site) => site.value),
        statuses: "APPROVED",
      }

      await getContractNameList(filter)
        .then((contractList) => {
          contractList.forEach((contract) => {
            this.customerIdcIdList.push({
              id: contract.contractId,
              name: `${contract.contractId.toString().padStart(8, "0")}`,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    // get department list
    async getDepartmentListFromApi(siteId) {
      this.showLoading(true)
      this.departmentList = []

      await getDepartmentListBySiteId(siteId)
        .then((departments) => {
          this.departmentList = departments.map((department) => {
            return {
              id: department.departmentId,
              name:
                department.departmentName +
                (department.isSupportDepartment ? " (Support)" : ""),
              isSupportDepartment: department.isSupportDepartment,
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    // get topic list
    async getTopicListFromApi() {
      this.showLoading(true)
      this.topicList = []

      await getTopicList()
        .then((topics) => {
          topics.forEach((topic) => {
            this.topicList.push({ id: topic.topicId, name: topic.topicName })
          })
        })
        .finally(() => this.showLoading(false))
    },

    // get customer data
    async getCustomerData(idcId) {
      this.showLoading(true)
      await getContractByIdcId(idcId)
        .then(async (contract) => {
          const emails = this.getUniqueArray(
            contract.contactPoints.map((c) => {
              if (c.contactType === "CUSTOMER") return c.contactEmail
            })
          )

          const phones = this.getUniqueArray(
            contract.contactPoints.map((c) => {
              if (c.contactType === "CUSTOMER") return c.contactPhone
            })
          )

          const siteName = contract.siteId
            ? await this.getSingleSiteNameBySiteId(contract.siteId)
            : "Site Not Found"

          this.customerData = {
            idcId: this.customerIdcIdList.find((idc) => idc.id === idcId),
            id: contract.customer.customerId,
            name: contract.customer.customerName,
            siteId: contract.siteId,
            siteName,
            email: emails.join(", "),
            phone: phones.join(", "),
          }

          if (contract?.escalationId && this.escalationTemplateList?.length) {
            this.escalationSelected = this.escalationTemplateList?.find(
              (item) => item.escalationId === contract?.escalationId
            )

            if (this.escalationSelected) {
              await this.getEscalationDetail(
                this.escalationSelected?.escalationId
              )
            }
          }

          await this.getDepartmentListFromApi(this.customerData.siteId)
        })
        .catch(() => {
          this.customerData = {
            idcId: this.customerIdcIdList.find((idc) => idc.id === idcId),
            id: null,
            name: null,
            siteId: null,
            siteName: "Site Not Found",
            email: null,
            phone: null,
          }
          this.departmentList = []
        })
        .finally(() => this.showLoading(false))
    },

    // get ticket data
    async getTicketData(id) {
      this.showLoading(true)
      await getTopicById(id)
        .then((topic) => {
          this.ticketData = {
            topic: this.topicList.find((t) => t.id === topic.topicId),
            subject: topic.topicSubject,
            detail: topic.topicDetail,
          }
        })
        .finally(() => this.showLoading(false))
    },

    // create ticket
    async createTicketToApi(createTicket, uploadAttachment) {
      this.showLoading(true)

      await createNewTicket(createTicket)
        .then((ticket) => {
          if (ticket.status === "ok") {
            this.toastSuccess("Ticket created successfully")

            if (uploadAttachment) {
              uploadTicketAttachments(
                ticket.data.ticketId,
                uploadAttachment
              ).then((attachment) => {
                if (attachment.status === "ok")
                  this.toastSuccess("Attachment uploaded successfully")
                else this.toastError("Attachment uploaded failed")
              })
              .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
                })
            }

            this.$router.push(
              `/internal/web-portal/ticket/${ticket.data.ticketId}`
            )
          } else this.toastError("Ticket created failed")
        })
        .catch((error) => this.toastError(error))
        .finally(() => this.showLoading(false))
    },

    // get unique array
    getUniqueArray(array) {
      return array.filter((item, position, self) => {
        return self.indexOf(item) === position
      })
    },

    async getTicketEscalationActiveList() {
      this.showLoading(true)
      await getTicketEscalationActiveList()
        .then((response) => {
          this.escalationTemplateList = response.data
        })
        .finally(() => this.showLoading(false))
    },

    changeEscalationTemplate(optionSelected) {
      this.escalationSelected = optionSelected
      this.escalationDetail = {
        name: "",
        description: "",
        levels: [],
      }

      if (
        this.escalationSelected?.escalationId &&
        this.escalationTemplateList.length
      ) {
        this.getEscalationDetail(this.escalationSelected?.escalationId)
      }
    },

    async getEscalationDetail(escalationId) {
      this.showLoading(true)
      await getTicketEscalationById(escalationId)
        .then((response) => {
          this.escalationDetail = response
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-create-new-ticket__container {
  padding: 0 0 21px 0;
}
</style>
